import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, SlidersHorizontal, ArrowRight, Loader2, X } from 'lucide-react'
import clsx from 'clsx'
import { fetchProducts, fetchCollections, formatPrice, getProductImage, type ShopifyProduct, type ShopifyCollection } from '../utils/shopify'
import { useSeo } from '../hooks/useSeo'

function ProductCard({ product }: { product: ShopifyProduct }) {
  const price = product.variants?.[0]?.price
  return (
    <Link to={`/products/${product.handle}`} className="card-hover group block">
      <div className="aspect-square relative overflow-hidden bg-brand-surface rounded-xl">
        <img
          src={getProductImage(product)}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-brand-purple rounded-full p-1.5">
            <ArrowRight size={14} className="text-white" />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <h3 className="font-medium text-sm truncate">{product.title}</h3>
        <p className="text-brand-purple text-sm font-semibold mt-0.5">
          {price ? formatPrice(price) : '—'}
        </p>
      </div>
    </Link>
  )
}

export default function CataloguePage() {
  useSeo({ title: 'Catalogue', description: 'Browse every MatryxLab mouse mat — 15 themed collections, 6 sizes, premium cloth or hard surface.', path: '/catalogue' })
  const [searchParams, setSearchParams] = useSearchParams()
  const [products, setProducts] = useState<ShopifyProduct[]>([])
  const [collections, setCollections] = useState<ShopifyCollection[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(searchParams.get('q') || '')
  const [activeCollection, setActiveCollection] = useState(searchParams.get('collection') || '')
  const [showFilters, setShowFilters] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [prods, cols] = await Promise.all([fetchProducts(100), fetchCollections()])
      setProducts(prods)
      setCollections(cols)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const filtered = products.filter(p => {
    const q = search.toLowerCase()
    const matchesSearch = !q || p.title.toLowerCase().includes(q) || (typeof p.tags === 'string' ? p.tags : p.tags?.join(',')).toLowerCase().includes(q)
    return matchesSearch
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchParams(search ? { q: search } : {})
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Catalogue</h1>
          <p className="text-brand-muted">Browse our full collection of premium mouse mats</p>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-brand-surface border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-brand-purple transition-colors"
            />
            {search && (
              <button type="button" onClick={() => { setSearch(''); setSearchParams({}) }} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X size={14} className="text-brand-muted" />
              </button>
            )}
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx('btn-secondary flex items-center gap-2 px-4', showFilters && 'border-brand-purple text-brand-purple')}
          >
            <SlidersHorizontal size={16} /> Filters
          </button>
        </div>

        {/* Collection filters */}
        {showFilters && collections.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCollection('')}
              className={clsx('px-3 py-1.5 rounded-full text-sm border transition-colors', !activeCollection ? 'bg-brand-purple border-brand-purple text-white' : 'border-white/10 hover:border-brand-purple/50')}
            >
              All
            </button>
            {collections.map(col => (
              <button
                key={col.id}
                onClick={() => setActiveCollection(col.handle === activeCollection ? '' : col.handle)}
                className={clsx('px-3 py-1.5 rounded-full text-sm border transition-colors', activeCollection === col.handle ? 'bg-brand-purple border-brand-purple text-white' : 'border-white/10 hover:border-brand-purple/50')}
              >
                {col.title}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-brand-purple" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-brand-muted">
            <p className="text-lg mb-2">No products found</p>
            <button onClick={() => { setSearch(''); setActiveCollection('') }} className="text-brand-purple hover:underline text-sm">Clear filters</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-brand-muted mb-4">{filtered.length} product{filtered.length !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(product => <ProductCard key={product.id} product={product} />)}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
